import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from "@angular/common";
import { FormGroup, FormBuilder } from "@angular/forms";
//import { ColDef, GridOptions } from '@ag-grid-enterprise/all-modules';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { Subscription, of, BehaviorSubject} from "rxjs";
import { catchError, finalize, tap  } from "rxjs/operators";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { DateService } from "../../../../common/services/date.service";

import { ApplicationLogResult, LogSearchSpec, LogEventLevel } from "../../../models/admin-reports.model";
import { AdminReportsService } from "../../../services/admin-reports.service";


@Component({
    selector: 'app-application-log',
    templateUrl: './application-log.component.html',
    styleUrls: ['./application-log.component.scss'],
    standalone: false
})
export class ApplicationLogComponent implements OnInit, OnDestroy {

    data: ApplicationLogResult[];
    dataLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    form: FormGroup;

    get allowExport(): boolean {
        return this.dataLoaded.value;
    }

    futureDateLimit?: Date | null;
    pastDateLimit?: Date | null;

	gridOptions: GridOptions;
	gridApi?: GridApi;
    invalidDatesDialog: Dialog = new Dialog("When providing dates, both From and To dates must be provided, and the To date must be greater than or equal to the From date.", [new DialogButton("OK", false, () => { return false; })])
    levelOptions: any[] = [
        { id: "", text: "--Any--" },
        { id: LogEventLevel.Warning, text: LogEventLevel.Warning },
        { id: LogEventLevel.Error, text: LogEventLevel.Error },
        { id: LogEventLevel.Fatal, text: LogEventLevel.Fatal }
    ];


    notReadyDialog: Dialog = new Dialog("Report must finish loading before it can be exported.", [new DialogButton("OK", false, () => { return false; })]);

    private subheader = new SubHeader({ title: "Application Logs", buttons: [{ name: "Run Report", method: () => this.runReport() }, { name: "Export", method: () => this.export() }] });
    subscription: Subscription = new Subscription();

    constructor(private builder: FormBuilder, private subheaderSvc: SubHeaderService, private pipe: DatePipe, private dialogSvc: DynamicDialogService, private formatter: GridFormatterService, private dateService: DateService, private reportSvc: AdminReportsService) {
    }

    ngOnInit() {
        this.buildForm();
        this.subheaderSvc.updateSubHeader(this.subheader);
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, false);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
        this.gridOptions.suppressColumnVirtualisation = true;
		this.gridOptions.pagination = false;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.runReport();
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    buildForm(): void {
        let fromDate: Date = new Date();
        fromDate.setHours(0, 0, 0, 0);
        let toDate: Date = new Date();
        toDate.setHours(0, 0, 0, 0);
        this.form = this.builder.group({
            fromDate: this.builder.group({ datePicker: [ fromDate, []]}),
            toDate: this.builder.group({ datePicker: [ toDate , []] }),
            eventLevel: ["", []],
            messageText: ["", []],
            exceptionText: ["", []]
        });
    }

    createColumnDefinitions(): ColDef[] {
        return [
            {
                headerName: "Event Time",
                headerTooltip: "Event Time",
                field: "utcTimeStamp",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                valueFormatter: this.formatter.formatDate("utcTimeStamp", "MM/dd/yyyy hh:mm:ss a"),
                tooltipValueGetter: this.formatter.formatDate("utcTimeStamp", "MM/dd/yyyy hh:mm:ss a"),
                minWidth: 200,
                maxWidth: 200
            } as ColDef,
            {
                headerName: "Event Level",
                headerTooltip: "Event Level",
                field: "level",
                tooltipField: "level",
                minWidth: 125,
                maxWidth: 125
            } as ColDef,
            {
                headerName: "Message",
                headerTooltip: "Message",
                field: "message",
                tooltipField: "message",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 400
            } as ColDef,
            {
                headerName: "Exception",
                headerTooltip: "Exception",
                field: "exception",
                tooltipValueGetter: this.formatter.formatPotentiallyBlankField("exception", ""),
                valueFormatter: this.formatter.formatPotentiallyBlankField("exception", ""),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 400,
                maxWidth: 400
            }
        ] as ColDef[];
    }

    export(): void {
        if (this.allowExport) {
            let fromDate = this.form.get('fromDate').get('datePicker').value;
            if (fromDate != null) fromDate.setHours(0, 0, 0, 0);
            let toDate = this.form.get('toDate').get('datePicker').value;
            if (toDate != null) toDate.setHours(23, 59, 59, 59);
            let fileName = 'ApplicationLog';
            if (fromDate != null) fileName = `${fileName}_FROM_${this.pipe.transform(fromDate, "yyyy-MM-dd")}`;
            if (toDate != null) fileName = `${fileName}_TO_${this.pipe.transform(toDate, "yyyy-MM-dd")}`;
            const sheetName = 'Application Log';
            this.formatter.exportDataToExcel(fileName, sheetName, this.gridApi);
        } else {
            this.notReadyDialog.toggleDialog(true);
            this.dialogSvc.updateDialog(this.notReadyDialog);
        }
    }

    fromDateChange(evt: Date | null) : void {
        if (evt != null) {
            this.pastDateLimit = evt;
            this.pastDateLimit.setDate(evt.getDate() + 1);
        } else {
            this.pastDateLimit = null;
        }
    }

    runReport(): void {
        let fromDate = this.form.get('fromDate').get('datePicker').value;
        if (fromDate != null) fromDate.setHours(0, 0, 0, 0);
        let toDate = this.form.get('toDate').get('datePicker').value;
        if (toDate != null) toDate.setHours(23, 59, 59, 59);
        if ((fromDate == null && toDate == null) || (toDate > fromDate && fromDate != null)) {
            let spec: LogSearchSpec = {
                startDate: fromDate,
                endDate: toDate,
                messageSearchString: this.form.get('messageText').value,
                exceptionString: this.form.get('exceptionText').value,
                logEventLevel: this.form.get('eventLevel').value
            } as LogSearchSpec;
            if (this.gridOptions && this.gridApi) this.gridApi.setGridOption("loading", true);
            this.dataLoaded.next(false);
            let obs$ = this.reportSvc.searchApplicationLogs(spec).pipe(                
                catchError((err) => {
                    this.dialogSvc.showError();
                    console.error(err);
                    return of([]);
                }),
                finalize(() => {
                    this.dataLoaded.next(true);
                    if (this.gridOptions && this.gridApi) {
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                })
            );

            let subs$ = obs$.subscribe(
                (rows: ApplicationLogResult[]) => {
                    this.data = rows;
                    if (this.gridApi) {
                        this.gridApi.setGridOption("rowData", this.data);
						this.gridApi.setGridOption("loading", false);
						this.gridApi.autoSizeAllColumns();
                    }
                },
                (error) => {
                    console.error(error);
                    this.dialogSvc.showError();
                }
            );
            this.subscription.add(subs$);
        } else {
            this.invalidDatesDialog.toggleDialog(true);
            this.dialogSvc.updateDialog(this.invalidDatesDialog);
        }
       
    }

    toDateChange(evt: Date | null): void {
        if (evt != null) {
            this.futureDateLimit = evt;
            this.futureDateLimit.setDate(evt.getDate() - 1);
        } else {
            this.futureDateLimit = null;
        }
    }
}
